import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ProductDetail, CoatingCompatibility, Comment } from '../../../../types/ProductTypes';
import { SectionCard, SectionTitle, FullWidthFields } from '../../Section.styles';
import useGetCoatingNotes, { CoatingNote } from '../../../../api/useGetCoatingNotes';
import useGetCoatings from '../../../../api/useGetCoatings';
import { useEditMode } from '../../../../context/EditModeContext';
import { useAuthStore } from '../../../../context/AuthContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import useApi from '../../../../api/useApi';
import { useAppSettings } from '../../../../hooks/useAppSettings';
import { buildApiUrl } from '../../../../utils/apiUtils';
import { useDownloadFile } from '../../../../api/useDownloadFile';
import { mapProductFromApi } from '../../../../factory/ProductFactory';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import {
    CoatingTable,
    CoatingName,
    CompatibilityBadge,
    CoatingNotesList,
    CoatingNoteLink,
    ManufacturerLink,
    ManufacturerName,
    ManufacturerPopoverWrapper,
    ManufacturerPopoverOverlay,
    ManufacturerPopover,
    TemperatureCell,
    NoCoatingsMessage,
    CoatingCommentsSection,
    CommentsLabel,
    CommentsText,
    CommentsTextarea,
    EditSelect,
    EditInput,
    RemoveButton,
    ActionCell,
    NotesEditContainer,
    NoteItem,
    NoteNumber,
    NoteSelect,
    RemoveNoteButton,
    AnnexIIInfoMessage,
    AddCoatingRow,
    AddCoatingSelect,
    AddButton,
} from './CoatingCompatibilitiesSection.styles';

interface CoatingCompatibilitiesSectionProps {
    product: ProductDetail;
    onCoatingCompatibilitiesChange?: (coatingCompatibilities: CoatingCompatibility[]) => void;
    onCommentsChange?: (comments: Comment[]) => void;
    onCategoryClick?: () => void;
    categoryProductName?: string;
    categoryProductId?: number;
}

// Compatibility values matching the old implementation enum
enum CompatibilityLevel {
    UNKNOWN = -1,
    NOT_ASSESSED = 0,
    YES = 1,
    RESTRICTIONS = 2,
    NO = 3,
}

const compatibilityTextMap: Record<CompatibilityLevel, string> = {
    [CompatibilityLevel.UNKNOWN]: '?',
    [CompatibilityLevel.NOT_ASSESSED]: 'Not Assessed',
    [CompatibilityLevel.YES]: 'Yes',
    [CompatibilityLevel.RESTRICTIONS]: 'Restrictions',
    [CompatibilityLevel.NO]: 'No',
};

const compatibilityOptions = [
    { value: CompatibilityLevel.UNKNOWN, text: compatibilityTextMap[CompatibilityLevel.UNKNOWN] },
    { value: CompatibilityLevel.NOT_ASSESSED, text: compatibilityTextMap[CompatibilityLevel.NOT_ASSESSED] },
    { value: CompatibilityLevel.YES, text: compatibilityTextMap[CompatibilityLevel.YES] },
    { value: CompatibilityLevel.RESTRICTIONS, text: compatibilityTextMap[CompatibilityLevel.RESTRICTIONS] },
    { value: CompatibilityLevel.NO, text: compatibilityTextMap[CompatibilityLevel.NO] },
];

// Manufacturer logo/link configuration (matching old implementation)
const manufacturerConfig: Record<
    number,
    {
        name: string;
        logo: string;
        url: string;
        resistanceList?: string;
        generalNote?: string;
    }
> = {
    1: {
        name: 'Sigma',
        logo: 'Sigma_logo.png',
        url: 'http://tris.sigmacoatings.com/',
        resistanceList: 'Resistance list Sigma.pdf',
    },
    2: {
        name: 'Carboline',
        logo: 'Carboline_logo.png',
        url: 'http://www.carboline.com/',
        resistanceList: 'Resistance list Carboline.pdf',
    },
    3: {
        name: 'International',
        logo: 'International_logo.png',
        url: 'http://www.international-marine.com/supportadvice/cargo-resistance-guide.aspx',
        resistanceList: 'Resistance list International.pdf',
    },
    4: {
        name: 'Amercoat',
        logo: 'PPG_Amercoat_logo.gif',
        url: 'http://ppgamercoatus.ppgpmc.com/',
        resistanceList: 'Resistance list Amercoat.pdf',
    },
    5: {
        name: 'Jotun',
        logo: 'Jotun_logo.png',
        url: 'http://jotunprl.com/Search.aspx?c=1',
        resistanceList: 'Resistance list Jotun.pdf',
    },
    7: {
        name: 'Advanced Polymer Coatings',
        logo: 'APC_logo.gif',
        url: 'http://www.adv-polymer.com/Marine_Transportation_Protective_Coatings/MarineLine784.asp',
        resistanceList: 'Resistance list Advanced Polymer Coatings.pdf',
    },
    10: {
        name: 'Jotun Tankguard Flexline',
        logo: 'Jotun_logo.png',
        url: 'http://jotunprl.com/Search.aspx?c=1',
        resistanceList: 'Resistance list Jotun Tankguard Flexline.pdf',
    },
    11: {
        name: 'Chugoku',
        logo: 'cmp_logo.png',
        url: 'https://www.cmp-chugoku.com/global.html',
        resistanceList: 'Resistance list Chugoku.pdf',
    },
};

const CoatingCompatibilitiesSection: React.FC<CoatingCompatibilitiesSectionProps> = ({
    product,
    onCoatingCompatibilitiesChange,
    onCommentsChange,
    onCategoryClick,
    categoryProductName,
    categoryProductId,
}) => {
    const { isInEditMode } = useEditMode();
    const { canEdit } = useAuthStore();

    // ProductEditContext may not be available if provider is not in the tree
    const productEditContext = useProductEdit();
    const mergePendingChanges = productEditContext?.mergePendingChanges;
    const mergePendingChangesSilently = productEditContext?.mergePendingChangesSilently;
    const getPendingChanges = productEditContext?.getPendingChanges;

    // Fetch all coating notes for the dropdown
    const { coatingNotes: allCoatingNotes, getCoatingNotes } = useGetCoatingNotes();

    // Fetch all coatings for the "Add coating" dropdown
    const { coatings: allCoatings, getCoatings } = useGetCoatings();

    // For Annex II trade name products, fetch the category product's coating data
    const { getApiData } = useApi();
    const { settings: appSettings } = useAppSettings();
    const [categoryCoatings, setCategoryCoatings] = useState<CoatingCompatibility[] | null>(null);
    const [categoryComments, setCategoryComments] = useState<Comment[] | null>(null);

    const isProductCategory = !product.tradeName;
    const marpolRegulation = product.category?.regulations?.marpolRegulation;
    const isAnnexII = marpolRegulation === 2 || marpolRegulation === 3;
    const needsCategoryData = !isProductCategory && isAnnexII;

    useEffect(() => {
        if (!needsCategoryData || !categoryProductId || !appSettings?.api) return;

        const fetchCategoryProduct = async () => {
            try {
                const result = await getApiData({
                    endpoint: buildApiUrl(appSettings.api, `/GetProduct?id=${categoryProductId}`),
                });
                if (result) {
                    const mapped = mapProductFromApi(result);
                    setCategoryCoatings(mapped.coatingCompatibilities || []);
                    setCategoryComments(mapped.comments || []);
                }
            } catch (error) {
                console.error('[CoatingCompatibilitiesSection] Failed to fetch category product:', error);
            }
        };

        fetchCategoryProduct();
    }, [needsCategoryData, categoryProductId, appSettings?.api, getApiData]);

    // Local state for editing
    // For Annex II trade name products, use the category product's coating data
    const effectiveCoatings = useMemo(() =>
        needsCategoryData && categoryCoatings !== null
            ? categoryCoatings
            : product.coatingCompatibilities || [],
        [needsCategoryData, categoryCoatings, product.coatingCompatibilities]
    );
    const effectiveComments = useMemo(() =>
        needsCategoryData && categoryComments !== null
            ? categoryComments
            : product.comments || [],
        [needsCategoryData, categoryComments, product.comments]
    );

    const [localCoatings, setLocalCoatings] = useState<CoatingCompatibility[]>(effectiveCoatings);
    const [localComments, setLocalComments] = useState<Comment[]>(effectiveComments);

    // Fetch coating notes and all coatings on mount
    // All coatings are needed to show unconfigured coatings as '?' rows (matching old app behavior)
    useEffect(() => {
        getCoatingNotes();
        getCoatings();
    }, [getCoatingNotes, getCoatings]);

    // Re-fetch when entering edit mode to ensure fresh data
    useEffect(() => {
        if (isInEditMode && canEdit) {
            console.log('[CoatingSection] Fetching coating notes and coatings...');
            getCoatingNotes();
            getCoatings();
        }
    }, [isInEditMode, canEdit, getCoatingNotes, getCoatings]);


    // State for "Add new coating" row
    const [selectedNewCoatingId, setSelectedNewCoatingId] = useState<number | null>(null);
    const [selectedNewCompatibility, setSelectedNewCompatibility] = useState<number>(CompatibilityLevel.UNKNOWN);
    const [newMaxTemperature, setNewMaxTemperature] = useState<string>('');
    const [newCoatingNotes, setNewCoatingNotes] = useState<CoatingNote[]>([]);

    // Selectable coatings: active coatings not already in the list
    const selectableCoatings = useMemo(() => {
        const existingCoatingIds = new Set(localCoatings.map((c) => c.coating.id ?? (c.coating as any).Id));
        return allCoatings
            .filter((c) => c.isActive && !existingCoatingIds.has(c.id))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [allCoatings, localCoatings]);

    // Selectable notes for the "Add new coating" row (filtered by selected coating's manufacturer)
    const newRowSelectableNotes = useMemo(() => {
        if (!selectedNewCoatingId) return [];
        const selected = selectableCoatings.find((c) => c.id === selectedNewCoatingId);
        if (!selected) return [];
        const existingNoteIds = new Set(newCoatingNotes.map((n) => n.id));
        return allCoatingNotes.filter(
            (note) => note.manufacturerId === selected.manufacturerId && !existingNoteIds.has(note.id)
        );
    }, [selectedNewCoatingId, selectableCoatings, allCoatingNotes, newCoatingNotes]);

    // Reset notes when coating selection changes
    useEffect(() => {
        setNewCoatingNotes([]);
    }, [selectedNewCoatingId]);

    // Update local state when product or category data changes
    React.useEffect(() => {
        setLocalCoatings(effectiveCoatings);
    }, [effectiveCoatings]);

    React.useEffect(() => {
        setLocalComments(effectiveComments);
    }, [effectiveComments]);

    // Build the full coating list: existing active compatibilities + unconfigured active coatings as '?' rows
    // This matches the old app's CoatingCompatibilitiesWithOrWithoutConfiguration behavior
    const activeCoatings = useMemo(() => {
        const existingActive = localCoatings.filter((c) => c.coating.isActive);
        const existingCoatingIds = new Set(localCoatings.map((c) => c.coating.id ?? (c.coating as any).Id));

        // Add unconfigured active coatings as '?' (UNKNOWN = -1) rows
        const unconfiguredCoatings: CoatingCompatibility[] = allCoatings
            .filter((c) => c.isActive && !existingCoatingIds.has(c.id))
            .map((c) => ({
                id: 0,
                coating: {
                    id: c.id,
                    name: c.name,
                    type: c.type,
                    manufacturer: { id: c.manufacturerId, name: c.manufacturerName, lastUpdatedUtc: new Date().toISOString() },
                    isActive: c.isActive,
                    lastUpdatedUtc: new Date().toISOString(),
                    externalId: '',
                },
                compatibility: CompatibilityLevel.UNKNOWN,
                maxTemperature: null,
                coatingNotes: [],
                lastUpdatedUtc: new Date().toISOString(),
            }));

        return [...existingActive, ...unconfiguredCoatings];
    }, [localCoatings, allCoatings]);

    // Get comments - Category 2 (Coating Comments) and Category 14 (Coating Comments MSG)
    const coatingComments = localComments.find((c) => c.category === 2);
    const coatingCommentsMSG = localComments.find((c) => c.category === 14);

    // Notify parent of coating changes
    const notifyCoatingChange = useCallback(
        (updatedCoatings: CoatingCompatibility[]) => {
            setLocalCoatings(updatedCoatings);
            if (mergePendingChanges) {
                mergePendingChanges({ coatingCompatibilities: updatedCoatings });
            }
            if (onCoatingCompatibilitiesChange) {
                onCoatingCompatibilitiesChange(updatedCoatings);
            }
        },
        [onCoatingCompatibilitiesChange, mergePendingChanges]
    );

    // Auto-merge unconfigured active coatings into localCoatings when entering edit mode
    // for default Annex II products. This ensures all coatings (including ?/UNKNOWN ones) are
    // part of the save payload so the "all options must be in the list" validation passes.
    // Uses mergePendingChangesSilently so the form isn't marked as user-modified on load.
    const [hasAutoMerged, setHasAutoMerged] = useState(false);

    useEffect(() => {
        if (!isInEditMode) {
            setHasAutoMerged(false);
            return;
        }
        if (!canEdit || !isProductCategory || !isAnnexII || allCoatings.length === 0 || hasAutoMerged) return;

        const existingCoatingIds = new Set(localCoatings.map((c) => c.coating.id ?? (c.coating as any).Id));
        const missingCoatings = allCoatings.filter((c) => c.isActive && !existingCoatingIds.has(c.id));

        if (missingCoatings.length > 0) {
            const newEntries: CoatingCompatibility[] = missingCoatings.map((c) => ({
                id: 0,
                coating: {
                    id: c.id,
                    name: c.name,
                    type: c.type,
                    manufacturer: { id: c.manufacturerId, name: c.manufacturerName, lastUpdatedUtc: new Date().toISOString() },
                    isActive: c.isActive,
                    lastUpdatedUtc: new Date().toISOString(),
                    externalId: '',
                },
                compatibility: CompatibilityLevel.UNKNOWN,
                maxTemperature: null,
                coatingNotes: [],
                lastUpdatedUtc: new Date().toISOString(),
            }));
            const merged = [...localCoatings, ...newEntries];
            // Update local state for display
            setLocalCoatings(merged);
            // Push into pending changes for save payload, but don't mark form as user-modified
            if (mergePendingChangesSilently) {
                mergePendingChangesSilently({ coatingCompatibilities: merged });
            }
            if (onCoatingCompatibilitiesChange) {
                onCoatingCompatibilitiesChange(merged);
            }
        }
        setHasAutoMerged(true);
    }, [isInEditMode, canEdit, isProductCategory, isAnnexII, allCoatings, hasAutoMerged, localCoatings, mergePendingChangesSilently, onCoatingCompatibilitiesChange]);

    // Notify parent of comment changes
    const notifyCommentChange = useCallback(
        (updatedComments: Comment[]) => {
            setLocalComments(updatedComments);
            if (mergePendingChanges) {
                // Get current pending changes to properly merge comments from other sections
                const currentPendingChanges = getPendingChanges ? getPendingChanges() : {};
                const baseComments = currentPendingChanges.comments || product.comments || [];

                // Find what categories we're updating (2 and 14 are coating comment categories)
                const updatingCategories = new Set(updatedComments.map((c) => c.category));

                // Keep comments from other categories that aren't being updated
                const otherComments = baseComments.filter((c: Comment) => !updatingCategories.has(c.category));

                // Merge: keep other section's comments + our updated comments
                const mergedComments = [...otherComments, ...updatedComments];

                mergePendingChanges({ comments: mergedComments });
            } else {
                console.warn('[CoatingCompatibilitiesSection.notifyCommentChange] mergePendingChanges is not available!');
            }
            if (onCommentsChange) {
                onCommentsChange(updatedComments);
            }
        },
        [onCommentsChange, mergePendingChanges, getPendingChanges, product.comments]
    );

    // Handle compatibility change (use coating.id as unique identifier, not compatibility.id which is 0 for new entries)
    const handleCompatibilityChange = useCallback(
        (coatingEntityId: number, newValue: number) => {
            const exists = localCoatings.some((c) => c.coating.id === coatingEntityId);
            if (exists) {
                const updatedCoatings = localCoatings.map((c) => (c.coating.id === coatingEntityId ? { ...c, compatibility: newValue } : c));
                notifyCoatingChange(updatedCoatings);
            } else {
                // Coating is an unconfigured '?' row from activeCoatings — promote it into localCoatings
                const entry = activeCoatings.find((c) => c.coating.id === coatingEntityId);
                if (entry) {
                    notifyCoatingChange([...localCoatings, { ...entry, compatibility: newValue }]);
                }
            }
        },
        [localCoatings, activeCoatings, notifyCoatingChange]
    );

    // Handle max temperature change (use coating.id as unique identifier)
    const handleMaxTemperatureChange = useCallback(
        (coatingEntityId: number, newValue: string) => {
            const numValue = newValue === '' ? null : parseFloat(newValue);
            const exists = localCoatings.some((c) => c.coating.id === coatingEntityId);
            if (exists) {
                const updatedCoatings = localCoatings.map((c) => (c.coating.id === coatingEntityId ? { ...c, maxTemperature: numValue } : c));
                notifyCoatingChange(updatedCoatings);
            } else {
                const entry = activeCoatings.find((c) => c.coating.id === coatingEntityId);
                if (entry) {
                    notifyCoatingChange([...localCoatings, { ...entry, maxTemperature: numValue }]);
                }
            }
        },
        [localCoatings, activeCoatings, notifyCoatingChange]
    );

    // Handle remove coating compatibility (use coating.id as unique identifier)
    const handleRemoveCoating = useCallback(
        (coatingEntityId: number) => {
            const updatedCoatings = localCoatings.filter((c) => c.coating.id !== coatingEntityId);
            notifyCoatingChange(updatedCoatings);
        },
        [localCoatings, notifyCoatingChange]
    );

    // Handle add coating note (use coating.id as unique identifier)
    const handleAddCoatingNote = useCallback(
        (coatingEntityId: number, noteId: number) => {
            const noteToAdd = allCoatingNotes.find((n) => n.id === noteId);
            if (!noteToAdd) return;

            const exists = localCoatings.some((c) => c.coating.id === coatingEntityId);
            if (exists) {
                const updatedCoatings = localCoatings.map((c) => {
                    if (c.coating.id === coatingEntityId) {
                        const existingNotes = c.coatingNotes || [];
                        if (existingNotes.some((n: any) => n.id === noteId)) {
                            return c;
                        }
                        return {
                            ...c,
                            coatingNotes: [...existingNotes, noteToAdd],
                        };
                    }
                    return c;
                });
                notifyCoatingChange(updatedCoatings);
            } else {
                const entry = activeCoatings.find((c) => c.coating.id === coatingEntityId);
                if (entry) {
                    const existingNotes = entry.coatingNotes || [];
                    if (!existingNotes.some((n: any) => n.id === noteId)) {
                        notifyCoatingChange([...localCoatings, { ...entry, coatingNotes: [...existingNotes, noteToAdd] }]);
                    }
                }
            }
        },
        [localCoatings, activeCoatings, allCoatingNotes, notifyCoatingChange]
    );

    // Handle remove coating note (use coating.id as unique identifier)
    const handleRemoveCoatingNote = useCallback(
        (coatingEntityId: number, noteId: number) => {
            const exists = localCoatings.some((c) => c.coating.id === coatingEntityId);
            if (exists) {
                const updatedCoatings = localCoatings.map((c) => {
                    if (c.coating.id === coatingEntityId) {
                        return {
                            ...c,
                            coatingNotes: (c.coatingNotes || []).filter((n: any) => n.id !== noteId),
                        };
                    }
                    return c;
                });
                notifyCoatingChange(updatedCoatings);
            } else {
                const entry = activeCoatings.find((c) => c.coating.id === coatingEntityId);
                if (entry) {
                    notifyCoatingChange([...localCoatings, { ...entry, coatingNotes: (entry.coatingNotes || []).filter((n: any) => n.id !== noteId) }]);
                }
            }
        },
        [localCoatings, activeCoatings, notifyCoatingChange]
    );

    // Get selectable notes for a coating (filtered by manufacturer)
    const getSelectableNotes = useCallback(
        (compatibility: CoatingCompatibility): CoatingNote[] => {
            const manufacturerId = compatibility.coating.manufacturer.id;
            const existingNoteIds = (compatibility.coatingNotes || []).map((n: any) => n.id);

            return allCoatingNotes.filter((note) => note.manufacturerId === manufacturerId && !existingNoteIds.includes(note.id));
        },
        [allCoatingNotes]
    );

    // Handle add new coating compatibility
    const handleAddCoating = useCallback(() => {
        if (!selectedNewCoatingId) return;
        const coatingItem = allCoatings.find((c) => c.id === selectedNewCoatingId);
        if (!coatingItem) return;

        const newCoatingCompatibility: CoatingCompatibility = {
            id: 0, // New entry, server will assign ID
            coating: {
                id: coatingItem.id,
                name: coatingItem.name,
                type: coatingItem.type,
                manufacturer: {
                    id: coatingItem.manufacturerId,
                    name: coatingItem.manufacturerName,
                    lastUpdatedUtc: new Date().toISOString(),
                },
                isActive: coatingItem.isActive,
                lastUpdatedUtc: new Date().toISOString(),
                externalId: '',
            },
            compatibility: selectedNewCompatibility,
            maxTemperature: newMaxTemperature === '' ? null : parseFloat(newMaxTemperature),
            coatingNotes: newCoatingNotes,
            lastUpdatedUtc: new Date().toISOString(),
        };

        const updatedCoatings = [...localCoatings, newCoatingCompatibility];
        notifyCoatingChange(updatedCoatings);

        // Reset the add-row inputs
        setSelectedNewCoatingId(null);
        setSelectedNewCompatibility(CompatibilityLevel.UNKNOWN);
        setNewMaxTemperature('');
        setNewCoatingNotes([]);
    }, [selectedNewCoatingId, selectedNewCompatibility, newMaxTemperature, newCoatingNotes, allCoatings, localCoatings, notifyCoatingChange]);

    // Handle comment change
    const handleCommentChange = useCallback(
        (category: number, newText: string) => {
            const existingComment = localComments.find((c) => c.category === category);
            let updatedComments: Comment[];

            if (existingComment) {
                updatedComments = localComments.map((c) => (c.category === category ? { ...c, text: newText } : c));
            } else {
                // Create new comment if it doesn't exist
                const newComment: Comment = {
                    id: 0, // New comment, will be assigned by server
                    text: newText,
                    category: category,
                    visibility: 1,
                    lastUpdatedUtc: new Date().toISOString(),
                };
                updatedComments = [...localComments, newComment];
            }
            notifyCommentChange(updatedComments);
        },
        [localComments, notifyCommentChange]
    );

    const getCompatibilityText = (level: number): string => {
        return compatibilityTextMap[level as CompatibilityLevel] ?? compatibilityTextMap[CompatibilityLevel.NOT_ASSESSED];
    };

    const getCompatibilityStyle = (level: number): React.CSSProperties => {
        switch (level) {
            case 1: // Yes
                return { color: 'green', fontWeight: 600 };
            case 2: // Restrictions
                return { color: 'maroon', fontWeight: 600 };
            case 3: // No
                return { color: 'red', fontWeight: 700 };
            case -1: // Unknown
                return { color: 'red', fontWeight: 700 };
            default: // Not Assessed
                return { color: '#333' };
        }
    };

    const hasManufacturerInfo = (manufacturerId: number): boolean => {
        return manufacturerId in manufacturerConfig;
    };

    // Popover state for manufacturer click
    const [openPopoverManufacturerId, setOpenPopoverManufacturerId] = useState<number | null>(null);

    // Build general note file paths by manufacturer ID from coating notes
    const generalNotesByManufacturerId = useMemo(() => {
        const map: Record<number, string> = {};
        allCoatingNotes.forEach((note) => {
            if (note.isGeneralNote && note.filePath) {
                map[note.manufacturerId] = note.filePath;
            }
        });
        return map;
    }, [allCoatingNotes]);

    const { downloadFile } = useDownloadFile();

    const handleDownloadFile = (category: string, fileName: string) => {
        downloadFile({ category, fileName }).catch((err) =>
            console.error('File download failed:', err)
        );
    };

    const renderManufacturer = (manufacturerId: number, manufacturerName: string) => {
        if (hasManufacturerInfo(manufacturerId)) {
            const config = manufacturerConfig[manufacturerId];
            const generalNoteFile = generalNotesByManufacturerId[manufacturerId];
            const isOpen = openPopoverManufacturerId === manufacturerId;

            return (
                <ManufacturerPopoverWrapper>
                    <ManufacturerLink
                        as="span"
                        onClick={() => setOpenPopoverManufacturerId(isOpen ? null : manufacturerId)}
                        title={`${config.name} - click for details`}
                    >
                        {manufacturerName}
                    </ManufacturerLink>
                    {isOpen && (
                        <>
                            <ManufacturerPopoverOverlay onClick={() => setOpenPopoverManufacturerId(null)} />
                            <ManufacturerPopover>
                                <div className="popover-title">Coating Manufacturer</div>
                                <div className="popover-body">
                                    <div className="logo-section">
                                        <a href={config.url} target="_blank" rel="noopener noreferrer">
                                            <img
                                                src={`/assets/images/${config.logo}`}
                                                alt={config.name}
                                            />
                                        </a>
                                    </div>
                                    <div className="links-section">
                                        {generalNoteFile && (
                                            <a onClick={() => handleDownloadFile('Coating', generalNoteFile)}>
                                                General Note
                                            </a>
                                        )}
                                        {config.resistanceList && (
                                            <a onClick={() => handleDownloadFile('Coating', config.resistanceList!)}>
                                                Resistance List
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </ManufacturerPopover>
                        </>
                    )}
                </ManufacturerPopoverWrapper>
            );
        }
        return <ManufacturerName>{manufacturerName}</ManufacturerName>;
    };

    // Annex II: only editable at default product (category) level
    // Annex I (and Gas/other): editable at both category and trade name level
    const showEditControls = isInEditMode && canEdit && (isProductCategory || !isAnnexII);

    // Default Annex II products: coatings cannot be removed (all must stay in the list)
    const canRemoveCoatings = showEditControls && !(isProductCategory && isAnnexII);

    // For Annex II trade name products, show read-only info message
    const showAnnexIIReadOnlyMessage = !isProductCategory && isAnnexII;

    return (
        <SectionCard>
            <SectionTitle>Coating Resistance List</SectionTitle>

            {showAnnexIIReadOnlyMessage && (
                <AnnexIIInfoMessage>
                    Coating resistance values are maintained at the default product level.
                    {onCategoryClick && (
                        <>{' '}<a onClick={onCategoryClick}>View {categoryProductName || 'default product'}</a></>
                    )}
                </AnnexIIInfoMessage>
            )}

            {activeCoatings.length === 0 && !showEditControls ? (
                <NoCoatingsMessage>No coating resistance information.</NoCoatingsMessage>
            ) : (
                <CoatingTable>
                    <thead>
                        <tr>
                            {showEditControls && <th className="action-col"></th>}
                            <th>Name</th>
                            <th>Resistance</th>
                            <th>Notes</th>
                            <th>Manufacturer</th>
                            <th className="max-temp-col">Max Temperature</th>
                        </tr>
                    </thead>
                    <tbody>
                        {activeCoatings
                            .sort((a, b) => a.coating.name.localeCompare(b.coating.name))
                            .map((compatibility, index) => (
                                <tr key={compatibility.id ? compatibility.id : `new-${index}-${compatibility.coating?.name}`}>
                                    {showEditControls && (
                                        <td>
                                            <ActionCell>
                                                {canRemoveCoatings && (
                                                    <RemoveButton
                                                        type="button"
                                                        onClick={() => handleRemoveCoating(compatibility.coating.id)}
                                                        title="Remove Coating Compatibility"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </RemoveButton>
                                                )}
                                            </ActionCell>
                                        </td>
                                    )}
                                    <td>
                                        <CoatingName>{compatibility.coating.name}</CoatingName>
                                    </td>
                                    <td>
                                        {showEditControls ? (
                                            <EditSelect
                                                value={compatibility.compatibility}
                                                onChange={(e) => handleCompatibilityChange(compatibility.coating.id, parseInt(e.target.value, 10))}
                                            >
                                                {compatibilityOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.text}
                                                    </option>
                                                ))}
                                            </EditSelect>
                                        ) : (
                                            <CompatibilityBadge style={getCompatibilityStyle(compatibility.compatibility)}>
                                                {getCompatibilityText(compatibility.compatibility)}
                                            </CompatibilityBadge>
                                        )}
                                    </td>
                                    <td>
                                        {showEditControls ? (
                                            <NotesEditContainer>
                                                {/* Show existing notes with remove buttons */}
                                                {compatibility.coatingNotes &&
                                                    compatibility.coatingNotes.map((note: any, index: number) => (
                                                        <NoteItem key={note.id || index}>
                                                            <RemoveNoteButton
                                                                type="button"
                                                                onClick={() => handleRemoveCoatingNote(compatibility.coating.id, note.id)}
                                                                title="Remove Coating Note"
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </RemoveNoteButton>
                                                            <NoteNumber>{note.number}</NoteNumber>
                                                        </NoteItem>
                                                    ))}
                                                {/* Dropdown to add new notes (filtered by manufacturer) */}
                                                {getSelectableNotes(compatibility).length > 0 && (
                                                    <NoteSelect
                                                        value=""
                                                        onChange={(e) => {
                                                            if (e.target.value) {
                                                                handleAddCoatingNote(compatibility.coating.id, parseInt(e.target.value, 10));
                                                            }
                                                        }}
                                                    >
                                                        <option value="">Add note...</option>
                                                        {getSelectableNotes(compatibility).map((note) => (
                                                            <option key={note.id} value={note.id}>
                                                                {note.number}
                                                            </option>
                                                        ))}
                                                    </NoteSelect>
                                                )}
                                            </NotesEditContainer>
                                        ) : compatibility.coatingNotes && compatibility.coatingNotes.length > 0 ? (
                                            <CoatingNotesList>
                                                {compatibility.coatingNotes.map((note: any, index: number) => (
                                                    <CoatingNoteLink
                                                        key={note.id || index}
                                                        title={`${note.manufacturer?.name || ''} - ${note.number}\n${note.text || ''}`}
                                                    >
                                                        {note.number}
                                                    </CoatingNoteLink>
                                                ))}
                                            </CoatingNotesList>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </td>
                                    <td>{renderManufacturer(compatibility.coating.manufacturer.id, compatibility.coating.manufacturer.name)}</td>
                                    <td>
                                        {showEditControls ? (
                                            <EditInput
                                                type="number"
                                                value={compatibility.maxTemperature ?? ''}
                                                onChange={(e) => handleMaxTemperatureChange(compatibility.coating.id, e.target.value)}
                                                placeholder=""
                                                min="-273"
                                                max="999"
                                                step="0.01"
                                            />
                                        ) : compatibility.maxTemperature !== null && compatibility.maxTemperature !== undefined ? (
                                            <TemperatureCell>{compatibility.maxTemperature}°C</TemperatureCell>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        {/* Add new coating row */}
                        {showEditControls && selectableCoatings.length > 0 && (
                            <tr>
                                <td></td>
                                <td>
                                    <AddCoatingSelect
                                        value={selectedNewCoatingId ?? ''}
                                        onChange={(e) => setSelectedNewCoatingId(e.target.value ? parseInt(e.target.value, 10) : null)}
                                    >
                                        <option value="">Select coating...</option>
                                        {selectableCoatings.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </AddCoatingSelect>
                                </td>
                                <td>
                                    <EditSelect
                                        value={selectedNewCompatibility}
                                        onChange={(e) => setSelectedNewCompatibility(parseInt(e.target.value, 10))}
                                    >
                                        {compatibilityOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.text}
                                            </option>
                                        ))}
                                    </EditSelect>
                                </td>
                                <td>
                                    <NotesEditContainer>
                                        {newCoatingNotes.map((note, index) => (
                                            <NoteItem key={note.id || index}>
                                                <RemoveNoteButton
                                                    type="button"
                                                    onClick={() => setNewCoatingNotes((prev) => prev.filter((n) => n.id !== note.id))}
                                                    title="Remove Coating Note"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </RemoveNoteButton>
                                                <NoteNumber>{note.number}</NoteNumber>
                                            </NoteItem>
                                        ))}
                                        {selectedNewCoatingId && newRowSelectableNotes.length > 0 && (
                                            <NoteSelect
                                                value=""
                                                onChange={(e) => {
                                                    if (e.target.value) {
                                                        const noteToAdd = allCoatingNotes.find((n) => n.id === parseInt(e.target.value, 10));
                                                        if (noteToAdd) {
                                                            setNewCoatingNotes((prev) => [...prev, noteToAdd]);
                                                        }
                                                    }
                                                }}
                                            >
                                                <option value="">Add note...</option>
                                                {newRowSelectableNotes.map((note) => (
                                                    <option key={note.id} value={note.id}>
                                                        {note.number}
                                                    </option>
                                                ))}
                                            </NoteSelect>
                                        )}
                                    </NotesEditContainer>
                                </td>
                                <td>
                                    {selectedNewCoatingId && (() => {
                                        const selected = selectableCoatings.find((c) => c.id === selectedNewCoatingId);
                                        return selected ? renderManufacturer(selected.manufacturerId, selected.manufacturerName) : null;
                                    })()}
                                </td>
                                <td>
                                    <AddCoatingRow>
                                        <EditInput
                                            type="number"
                                            value={newMaxTemperature}
                                            onChange={(e) => setNewMaxTemperature(e.target.value)}
                                            placeholder="°C"
                                            min="-273"
                                            max="999"
                                            step="0.01"
                                        />
                                        <AddButton
                                            type="button"
                                            onClick={handleAddCoating}
                                            disabled={!selectedNewCoatingId}
                                            title="Add Coating Compatibility"
                                        >
                                            <FontAwesomeIcon icon={faPlus} /> Add
                                        </AddButton>
                                    </AddCoatingRow>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </CoatingTable>
            )}

            {/* Coating Comments (Category 2) - visible in edit mode OR when text exists */}
            {(isInEditMode || (coatingComments && coatingComments.text)) && (
                <CoatingCommentsSection>
                    <CommentsLabel>Comments:</CommentsLabel>
                    <FullWidthFields>
                        {showEditControls ? (
                            <CommentsTextarea
                                rows={3}
                                value={coatingComments?.text || ''}
                                onChange={(e) => handleCommentChange(2, e.target.value)}
                                placeholder="Enter coating comments..."
                            />
                        ) : (
                            <CommentsText>{coatingComments?.text}</CommentsText>
                        )}
                    </FullWidthFields>
                </CoatingCommentsSection>
            )}

            {/* Coating Comments MSG (Category 14) - only visible to editors, in edit mode OR when text exists */}
            {canEdit && (isInEditMode || (coatingCommentsMSG && coatingCommentsMSG.text)) && (
                <CoatingCommentsSection>
                    <CommentsLabel>Comments (MSG):</CommentsLabel>
                    <FullWidthFields>
                        {showEditControls ? (
                            <CommentsTextarea
                                rows={3}
                                value={coatingCommentsMSG?.text || ''}
                                onChange={(e) => handleCommentChange(14, e.target.value)}
                                placeholder="Enter MSG coating comments..."
                            />
                        ) : (
                            <CommentsText>{coatingCommentsMSG?.text}</CommentsText>
                        )}
                    </FullWidthFields>
                </CoatingCommentsSection>
            )}
        </SectionCard>
    );
};

export default CoatingCompatibilitiesSection;
